import { lazy } from 'react';

/** sessionStorage key holding the epoch-ms of the last stale-chunk reload. */
export const CHUNK_RELOAD_KEY = 'dfq:chunk-reload-at';

/** A second failure inside this window is treated as a genuinely broken build. */
const RELOAD_WINDOW_MS = 10_000;

/**
 * Recovers from a stale-deploy dynamic-import failure by reloading once.
 *
 * After a redeploy, content-hashed chunks referenced by an already-open tab no
 * longer exist, so the route's `import()` rejects with "Failed to fetch
 * dynamically imported module". One reload fetches the fresh index.html and its
 * current chunk hashes, which resolves it. The timestamp guard caps recovery to
 * a single reload per {@link RELOAD_WINDOW_MS}: if the freshly reloaded build
 * still can't load the chunk, the error is rethrown rather than reload-looping.
 *
 * On the reload path it returns a never-resolving promise so nothing renders in
 * the moment before navigation takes over.
 */
export function handleChunkLoadError(error: unknown): Promise<never> {
  const now = Date.now();
  const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);

  if (now - lastReloadAt > RELOAD_WINDOW_MS) {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    window.location.reload();
    return new Promise<never>(() => {});
  }

  throw error;
}

/**
 * Drop-in replacement for `React.lazy` that auto-recovers from stale-chunk
 * fetch failures after a deploy. {@see handleChunkLoadError}.
 */
export function lazyWithReload(
  factory: Parameters<typeof lazy>[0],
): ReturnType<typeof lazy> {
  return lazy(() => factory().catch(handleChunkLoadError));
}
